import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import { useMediaQuery } from "react-responsive";
import { toast } from "react-toastify";
import axios from "axios";
import uuid from "react-uuid";
import moment from "moment";

import { IoMdMenu } from "react-icons/io";
import { FiMoon, FiSun } from "react-icons/fi";

import { getSetting } from "../../../store/scheduler";

import { SchedulerPageContainer, MobileMenu } from "./component/styled/scheduler";

import {
  isSameDay,
  eventsSorted,
  keysToRemove,
  isSameUser,
  getDateRange,
  transformEvents,
  getIdFromClass,
  isMonthDifferent,
} from "./component/function/common";
import { useModal } from "../../../hooks/useModal";
import { useDisableScroll } from "../../../hooks/useDisableScroll";

import Calendar from "./component/scheduler/calendar";
import Header from "./component/scheduler/header";
import SideMenu from "./component/scheduler/sideMenu";
import HamburgerMenu from "./component/scheduler/hamburgerMenu";
import HolidayLayer from "./component/scheduler/holidayLayer";
import ContextMenu from "./component/common/contextMenu";
import Event from "./component/scheduler/event";
import ScheduleList from "./component/scheduler/scheduleList";
import SelectPrint from "./component/scheduler/selectPrint";

const Scheduler = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const { modal, openModal, closeModal } = useModal();

  const isMobile = useMediaQuery({ query: "(max-width:767px)" });
  const isTablet = useMediaQuery({ query: "(max-width:1050px)" });

  const user = useSelector((state) => state?.user?.data[0]);
  const { mySchedSet, isDarkMode } = useSelector((state) => state.scheduler);
  const company = useSelector((state) => state?.company?.data);

  const calendarRef = useRef(null);
  const sideCalendarRef = useRef(null);

  const [onDate, setOnDate] = useState(moment().format("YYYY-MM-DD"));
  const [onPrevDate, setOnPrevDate] = useState(null);
  const [showEvent, setShowEvent] = useState(false);
  const [eventInfo, setEventInfo] = useState({});
  const [data, setData] = useState([]);
  const [userDate, setUserDate] = useState({
    year: Number(moment().format("YYYY")),
    month: Number(moment().format("MM")),
  });
  const [isBurgerOpen, setIsBurgerOpen] = useState(false);
  const [holiday, setHoliday] = useState(null);
  const [weather, setWeather] = useState([]);
  const [todayWeather, setTodayWeater] = useState({});
  const [isSearch, setIsSearch] = useState(false);
  const [searchTxt, setSearchTxt] = useState("");
  const [context, setContext] = useState({ x: 0, y: 0, date: "" });
  const [date, setDate] = useState(getDateRange());
  const [holidays, setHolidays] = useState([]);
  const [events, setEvents] = useState([]);
  const [todayEvents, setTodayEvents] = useState([]);
  const [openComponents, setOpenComponents] = useState({
    scheduleList: false,
    sideMenu: false,
  });
  const [openPrint, setOpenPrint] = useState(false);

  useDisableScroll();

  useEffect(() => {
    if (user) {
      initSettings();
    }
    const search = location?.state?.search;
    if (search) {
      setIsSearch(true);
    }

    const locationDate = location?.state?.onDate;
    if (locationDate) {
      setOnDate(locationDate);
    } else {
      getWeather();
      getSchedule();
    }
  }, []);

  const initSettings = async () => {
    const url = "/api/toktokSettingList";
    const req = {
      mberNo: user.mberNo,
    };
    try {
      const res = await axios.post(url, req);
      if (res.request.status === 200) {
        dispatch(getSetting(res.data));
      }
    } catch (error) {
      console.log(error);
    }
  };

  // 캘린더 일정 조회
  const getSchedule = async () => {
    try {
      const filterOrg = (org) => {
        if (!org) return [];
        return org.filter((item) => item.check).map((item) => item.orgNo);
      };

      const url = "/api/scheduleList";
      const body = {
        mberNo: user.mberNo,
        showPersonalYn: mySchedSet.personalYn ? "Y" : "N",
        cmpnyNos: filterOrg(mySchedSet.companyYnList),
        groupNos: filterOrg(mySchedSet.groupYnList),
        excludeUnrelated: mySchedSet.excludeUnrelated === "Y" ? true : false,
        completeYn: mySchedSet.completeYn || "D",
        holidayYn: "Y",
        startDt: date.startDt,
        endDt: date.endDt,
      };

      const res = await axios.post(url, body);
      const schedule = res.data;

      const { events, holidays } = await transformEvents(schedule, company);

      setEvents(events);
      setHolidays(holidays);
      setData([...events, ...holidays]);
      isToday([...events, ...holidays]);
    } catch (error) {
      console.error(error);
    }
  };

  // 날씨 정보 가져오기
  const getWeather = async () => {
    const url = "/api/weather";
    const req = {
      startDt: date.startDt,
      endDt: date.endDt,
    };

    const res = await axios.post(url, req);
    if (res.status === 200) {
      setWeather(res.data);
      updateWeather(moment(), res.data);
    }
  };

  // 오늘 날짜 데이터 가져오기
  const isToday = (events) => {
    const target = moment(onDate);

    const data = events.filter((event) => {
      const start = moment(event.startDt);
      const end = event.endDt ? moment(event.endDt) : null;
      return isSameDay(target, start, end);
    });
    const sorted = eventsSorted(data);

    setTodayEvents(sorted);
  };

  // 클릭 시 해당 날짜 배경색 변경
  useEffect(() => {
    if (onDate) {
      updateDate(onDate);
    }
    const isDifferent = isMonthDifferent(onDate, date);
    if (isDifferent) {
      const newRange = getDateRange(onDate);
      if (newRange.startDt !== date.startDt || newRange.endDt !== date.endDt) {
        setDate(newRange);
      }
    }
  }, [onDate]);

  useEffect(() => {
    getWeather();
  }, [date]);

  useEffect(() => {
    getSchedule();
  }, [date, mySchedSet]);

  const updateDate = (onDate) => {
    if (moment(onDate).isSame(onPrevDate, "day")) return;

    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.gotoDate(onDate);
    }
    if (sideCalendarRef.current) {
      const sideCalendarApi = sideCalendarRef.current.getApi();
      sideCalendarApi.gotoDate(onDate);
    }

    const prevDateElements = document.querySelectorAll(
      `[data-date="${moment(onPrevDate).format("YYYY-MM-DD")}"]`,
    );
    const currentDateElements = document.querySelectorAll(
      `[data-date="${moment(onDate).format("YYYY-MM-DD")}"]`,
    );

    prevDateElements.forEach((item) => item.classList.remove("selected"));
    currentDateElements.forEach((item) => item.classList.add("selected"));

    setOnPrevDate(onDate);
  };

  // 클릭한 날짜 날씨
  const updateWeather = (date, data) => {
    const today = moment(date).format("YYYY-MM-DD");
    const weather = data.filter((item) => item.date === today);

    if (weather.length) {
      setTodayWeater(weather[0]);
    } else {
      setTodayWeater(null);
    }
  };

  // 날짜 클릭
  const handleDateClick = async (arg, isSearchReset = false) => {
    const target = moment(arg);

    let sameDate = data.filter((event) =>
      isSameDay(target, moment(event.startDt), event.end ? moment(event.endDt) : null),
    );

    if (isSearch && !isSearchReset && searchTxt) {
      sameDate = sameDate.filter((event) => event.title.includes(searchTxt));
    }
    const sorted = eventsSorted(sameDate);

    setTodayEvents(sorted);
    setOnDate(arg);
    updateWeather(arg, weather);

    const [year, month] = target.format("YYYY-MM").split("-");
    setUserDate({
      year: Number(year),
      month: Number(month),
    });
  };

  // 이벤트 클릭
  const handleEventClick = (item) => {
    const event = getIdFromClass(data, item);

    if (!event) return;

    if (isTablet && !["S", "H"].includes(event.gbn)) {
      navigate(`/mypage/detailEvent/${event.scheduleNo}`, {
        state: {
          path: `/mypage/scheduler`,
          search: searchTxt || "",
          onDate,
        },
      });
      return;
    }
    if (isTablet && ["S", "H"].includes(event.gbn)) {
      return;
    }

    setEventInfo(event);
    setShowEvent(true);
  };

  // 이벤트 드래그앤드롭
  const handleEventDrag = (info) => {
    const event = getIdFromClass(data, info.el);

    if (!event) {
      info.revert();
      return;
    }

    if (!isSameUser(user, event)) {
      info.revert();
      toast.error("작성자가 아닙니다.");
      return;
    } else {
      const isDday = event.dday === "Y";
      openModal({
        isOpen: true,
        type: "confirmation",
        title: `날짜 변경`,
        message: [`${isDday ? "디데이를" : "일정을"} 변경하시겠습니까?`],
        handleConfirm: () => handleEventDrop(false, info),
        handleCancel: () => handleEventDrop(true, info),
      });
    }
  };

  // 이벤트 드래그앤드롭 취소/확인 버튼
  const handleEventDrop = (flag, info) => {
    if (flag) {
      info.revert();
    } else {
      const event = getIdFromClass(data, info.el);
      if (!event) return;

      const { delta } = info;

      const calculateNewDate = (date) => {
        return moment(date)
          .add(delta.days, "days")
          .add(delta.months, "months")
          .add(delta.years, "years")
          .format("YYYY-MM-DD HH:mm:ss");
      };

      const newStart = calculateNewDate(event.startDt);
      const newEnd = calculateNewDate(event.endDt);

      event.startDt = newStart;
      event.endDt = newEnd;

      submitEventDrop(event);
    }

    closeModal();
  };

  // 모달 확인 - 이벤트 드래그앤드롭
  const submitEventDrop = async (eventInfo) => {
    const url = "/api/schedule";
    const body = keysToRemove(eventInfo);

    if (body.workerList && Object.keys(body.workerList).length !== 0) {
      const workers = Object.keys(body.workerList);
      body.workerNos = workers;
    }

    const res = await axios.put(url, body);
    if (res.status === 200) {
      toast.success("일정이 변경되었습니다.", { toastId: "no-access" });
      setShowEvent(false);
      getSchedule();
    }
  };

  // 이벤트 삭제
  const submitDelete = async (event) => {
    const url = "/api/schedule";
    const body = keysToRemove(event);

    body.delYn = "Y";
    body.createMberId = user.mberNo;

    const res = await axios.put(url, body);
    if (res.status === 200) {
      toast.success(`${event.dday === "Y" ? "디데이가" : "일정이"} 삭제되었습니다.`);
      setShowEvent(false);
      getSchedule();
      closeModal();
    }
  };

  // 이벤트 나가기
  const exitSchedule = async (event) => {
    const url = "/api/schedule";
    const data = {
      ...event,
      workerNos: [...Object.keys(eventInfo.workerList)].filter(
        (mberNo) => parseInt(mberNo, 10) !== user.mberNo,
      ),
    };
    const body = keysToRemove(data);

    try {
      const res = await axios.put(url, body);
      if (res.status === 200) {
        setShowEvent(false);
        getSchedule();
        closeModal();
      }
    } catch (error) {
      console.log(error);
    }
  };

  // 시스템 화면 모드 변경
  const setScreenMode = async (screenMode) => {
    const url = "/api/toktokSetting";
    const body = {
      ...mySchedSet,
      screenMode,
      excludeUnrelated:
        typeof mySchedSet.excludeUnrelated === "string"
          ? mySchedSet.excludeUnrelated === "Y"
          : mySchedSet.excludeUnrelated,
    };
    const res = await axios.put(url, body);
    if (res.status === 200) {
      dispatch(getSetting(body));
    }
  };

  // 공휴일 팝업
  const isHolidayOpen = (target = null) => {
    if (target) {
      setHoliday(target);
    }
    if (isTablet) {
      setOpenComponents((data) => ({ ...data, scheduleList: false }));
    }
  };

  // 일정 클릭
  const clickEvent = async (item, searchTxt = "") => {
    setEventInfo(item);

    if (isTablet) {
      navigate(`/mypage/detailEvent/${item.scheduleNo}`, {
        state: {
          path: `/mypage/scheduler`,
          search: searchTxt || "",
          onDate,
        },
      });
    } else {
      setShowEvent(true);
    }
  };

  const addContextMenuListener = () => {
    const calendarEl = calendarRef.current?.getApi()?.el;
    if (calendarEl) {
      const handleContextMenu = (e) => {
        e.preventDefault();
        const clickedElement = e.target;

        if (clickedElement.classList.contains("fc-daygrid-day-frame")) {
          const date = clickedElement.closest(".fc-daygrid-day").getAttribute("data-date");
          setContext({ x: e.clientX, y: e.clientY, date: date });
        } else {
          setContext({ x: 0, y: 0, date: "" });
        }
      };

      calendarEl.removeEventListener("contextmenu", handleContextMenu);
      calendarEl.addEventListener("contextmenu", handleContextMenu);
    }
  };

  return (
    <SchedulerPageContainer className={isDarkMode} onContextMenu={(e) => e.preventDefault()}>
      <Header setScreenMode={setScreenMode} handlePrint={() => setOpenPrint(true)} />
      <div className="inner_content" id="schduler_content">
        {isMobile && !isSearch && (
          <MobileMenu className={isDarkMode}>
            <button onClick={() => setOpenComponents((data) => ({ ...data, sideMenu: true }))}>
              <IoMdMenu />
            </button>
            <button onClick={() => setScreenMode(isDarkMode ? 0 : 1)}>
              {isDarkMode ? <FiSun /> : <FiMoon />}
            </button>
          </MobileMenu>
        )}
        <SideMenu
          sideCalendarRef={sideCalendarRef}
          data={events}
          handleDateClick={handleDateClick}
          userDate={userDate}
          setUserDate={setUserDate}
          setOpenComponents={setOpenComponents}
          openComponents={openComponents}
          onDate={onDate}
          setOpenPrint={setOpenPrint}
        />
        <Calendar
          calendarRef={calendarRef}
          data={data}
          setOnPrevDate={setOnPrevDate}
          handleDateClick={handleDateClick}
          handleEventClick={handleEventClick}
          handleEventDrag={handleEventDrag}
          showEvent={showEvent}
          setOpenComponents={setOpenComponents}
          onDate={onDate}
          isBurgerOpen={isBurgerOpen}
          setIsBurgerOpen={setIsBurgerOpen}
          clickEvent={clickEvent}
          isSearch={isSearch}
          setIsSearch={setIsSearch}
          searchTxt={searchTxt}
          setSearchTxt={setSearchTxt}
          weather={weather}
          isHolidayOpen={isHolidayOpen}
          holidays={holidays}
          events={events}
          openComponents={openComponents}
          addContextMenuListener={addContextMenuListener}
        />
        <ScheduleList
          selected={todayEvents}
          isTablet={isTablet}
          setEventInfo={setEventInfo}
          onDate={onDate}
          isBurgerOpen={isBurgerOpen}
          setIsBurgerOpen={setIsBurgerOpen}
          isHolidayOpen={isHolidayOpen}
          todayWeather={todayWeather}
          clickEvent={clickEvent}
          openComponents={openComponents}
          setOpenComponents={setOpenComponents}
        />
        {showEvent && (
          <Event
            eventInfo={eventInfo}
            setEventInfo={setEventInfo}
            setShowEvent={setShowEvent}
            submitDelete={submitDelete}
            exitSchedule={exitSchedule}
            getSchedule={getSchedule}
            searchTxt={searchTxt}
            onDate={onDate}
          />
        )}
        {holiday && <HolidayLayer holiday={holiday} setHoliday={setHoliday} />}
        {!isTablet && (
          <HamburgerMenu
            onDate={moment(onDate).format("YYYY-MM-DD hh:00:00")}
            isBurgerOpen={isBurgerOpen}
            setIsBurgerOpen={setIsBurgerOpen}
          />
        )}
      </div>
      {modal}
      {context.x > 0 && context.y > 0 && <ContextMenu context={context} setContext={setContext} />}
      {openPrint && <SelectPrint setOpenPrint={setOpenPrint} />}
    </SchedulerPageContainer>
  );
};

export default Scheduler;
